/**
 * Briques communes de l'écran « Messages élèves » : libellés, styles et états
 * (chargement, erreur, rafraîchissement en échec).
 */
import { Loader2 } from 'lucide-react';
import {
  studentMessagingError,
  type ConversationKind,
} from '../api/studentMessaging';

/** Affiché quand le serveur n'a pas répondu ; sinon son message est repris tel quel. */
export const OFFLINE =
  'Connexion impossible. Vérifiez votre réseau, puis réessayez.';

export const KIND_LABELS: Record<ConversationKind, string> = {
  DIRECT: 'Élève',
  GROUP: 'Groupe',
  OFFICE: 'Secrétariat',
};

export const primaryButton =
  'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-sm transition-colors disabled:opacity-50';
export const secondaryButton =
  'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium transition-colors disabled:opacity-50';
// 16 px sur téléphone : en dessous, iOS zoome sur le champ à la saisie.
export const field =
  'w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500';
export const card = 'bg-white rounded-2xl border border-slate-200 shadow-sm';

/** Pastille de non-lus d'un échange. */
export function UnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  // `relative` : le texte pour lecteurs d'écran, hors flux, reste dans la pastille.
  return (
    <span className="relative inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-indigo-600 text-white text-[11px] font-semibold">
      <span aria-hidden="true">{count > 99 ? '99+' : count}</span>
      <span className="sr-only">
        {` · ${count} non lu${count > 1 ? 's' : ''}`}
      </span>
    </span>
  );
}

export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="p-6 text-center space-y-3">
      <p role="alert" className="text-sm text-red-600">
        {message}
      </p>
      <button type="button" className={secondaryButton} onClick={onRetry}>
        Réessayer
      </button>
    </div>
  );
}

/** Le contenu reste affiché : seul le dernier rafraîchissement a échoué. */
export function RefreshNotice({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <div
      role="status"
      className="flex items-center justify-between gap-3 px-4 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-800"
    >
      <span>{studentMessagingError(error, OFFLINE)}</span>
      <button
        type="button"
        onClick={onRetry}
        className="flex-shrink-0 font-semibold underline"
      >
        Réessayer
      </button>
    </div>
  );
}

export function Loading({ label }: { label: string }) {
  return (
    <p className="flex items-center justify-center gap-2 p-6 text-sm text-slate-500">
      <Loader2 size={16} className="animate-spin" />
      {label}
    </p>
  );
}
